import React, { useEffect, useState } from "react";
import { NavigateFunction, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { QRlogin, quickLogin, userLogin } from "../../services/auth.service";
import { toast } from "react-toastify";
import { useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Typography
} from '@mui/material';
import { useInfoStore } from "../../store/userInfo";
import { isBrowser } from 'react-device-detect';
import { coreInit, initOptions, loadNamespace } from "../../locales/i18n";
import { useOrderStore } from "../../store/order";
import { useMenuStore } from "../../store/menuState";
import { useAnalytics } from "../../analytics/AnalyticsContext";
import { logger } from "../../utils/logger";

type Props = {}
const MAX_RETRY_COUNT = 1; 
const Login: React.FC<Props> = () => {
  const { userInfo, setUserInfo, setIsLogin, setLoginMode, setIsAyce, setTableInfo } = useInfoStore();
  const { resetOrderCart } = useOrderStore();
  const { isNativeFlag, setSelectedTable } = useMenuStore()
  const navigate: NavigateFunction = useNavigate();
  // const [ , setOpen] = useState(false)
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0); // 재시도 횟수 관리
  const location = useLocation();
  const { recordLoginEvent } = useAnalytics();

  useEffect(() => {
    // WebView에서 전송된 커스텀 이벤트를 처리하는 리스너
    const handleCustomEvent = (event: any) => {
      logger.log('event', event)
      const { storedUserInfo, storedTableInfo } = event.detail;
      
      if(storedUserInfo && typeof storedUserInfo === 'string' && storedUserInfo.length) {
        const parseUserInfo = JSON.parse(storedUserInfo)
        if (parseUserInfo) {
          logger.log('유저 정보:', parseUserInfo);
          setUserInfo(parseUserInfo);
          quickLoginWithRetry(parseUserInfo.userid)
        }
      }
      if(storedTableInfo &&  typeof storedTableInfo === 'string' && storedTableInfo.length) {
        const parseTableInfo = JSON.parse(storedTableInfo)
        if (parseTableInfo) {
          logger.log('테이블 정보:', parseTableInfo);
          setTableInfo(parseTableInfo);
        }
      }
    };
  
    // 커스텀 이벤트 리스너 추가
    document.addEventListener('ReactNativeData', handleCustomEvent);
  
    // 컴포넌트 언마운트 시 리스너 제거
    return () => {
      document.removeEventListener('ReactNativeData', handleCustomEvent);
    };
  }, []);


  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    let parseParam = queryParams.get("param");
    logger.log('parseParam',parseParam)
    let param = null
    if(parseParam)
      param = encodeURIComponent(parseParam)
    logger.log(param)
    if(param) {
      qrlogin(param)
    } else {
      if(userInfo)
        quickLoginWithRetry(userInfo.userid);
      else 
        setLoading(false);
    }
  },[])

  const qrlogin = async (param: string) => {
      await QRlogin(param).then(
        res => {
          // Pinpoint 로그 기록 - QR 로그인 성공
          if(!isNativeFlag) {
            recordLoginEvent('qr', true, { storeId: res?.storeId || 'unknown' });
          } else {
            if (window.Android !== undefined)
              window.Android.postMessage(JSON.stringify({ type: 'attr', data: {function: 'qr', storeId: res?.storeId || 'unknown'} }));
          }

          setIsLogin(true);
          setUserInfo(res);
          setSelectedTable({id: res.tableId, tableName: res.tableName})
          setTableInfo({id: res.tableId, tableName: res.tableName})
          setLoginMode('qr');
          loadNamespace(`${res.storeId}`);
          coreInit(initOptions);
          resetOrderCart();
          if(res.isAyce) {
            setIsAyce(res.isAyce);
            navigate('/aycemenu')
          } else
            navigate('/main');
        }
      ).catch(error => {
        // Pinpoint 로그 기록 - QR 로그인 실패
        if(!isNativeFlag) {
          recordLoginEvent('qr', false, { storeId:'unknown', error: error.message || 'Unknown error' });
        } else {
          if (window.Android !== undefined)
            window.Android.postMessage(JSON.stringify({ type: 'attr', data: {function: 'qr', storeId: 'unknown', error: error.message || 'Unknown error'} }));
        }
        logger.error('QR 로그인 오류:', error);
      });
  }

  function quickLoginWithRetry(userId: string) {
    logger.log('userId', userId)
    if (retryCount >= MAX_RETRY_COUNT) {
      logger.log("Maximum retry attempts reached.");
      toast.error("Quick login failed after multiple attempts.");
      
      // Pinpoint 로그 기록 - 빠른 로그인 최대 시도 실패
      if(!isNativeFlag) {
        recordLoginEvent('quick_max_retry', false, { storeId:'unknown', userId, retryCount });
      } else {
        if (window.Android !== undefined)
          window.Android.postMessage(JSON.stringify({ type: 'attr', data: {function: 'quick_max_retry', storeId: 'unknown', userId, retryCount} }));
      }
      
      setLoading(false);
      return; // 최대 시도 횟수 초과 시 종료
    }
    if(userId === '') return
    quickLogin(userId).then(
      res => {
        logger.log(res);
        
        // Pinpoint 로그 기록 - 빠른 로그인 성공
        if(!isNativeFlag) {
          recordLoginEvent('quick', true, { userId: userId || 'unknown', storeId: res?.storeId || 'unknown' });
        } else {
          if (window.Android !== undefined)
            window.Android.postMessage(JSON.stringify({ type: 'attr', data: {function: 'quick', userId: userId || 'unknown', storeId: res?.storeId || 'unknown'} }));
        }
        
        setUserInfo(res);
        setIsLogin(true);
        if (res.isAyce) {
          setIsAyce(res.isAyce);
        }
        setLoginMode('quick');
        loadNamespace(`${res.storeId}`);
        coreInit(initOptions);
        resetOrderCart();
        navigate("/storesetting");
        try {
          if (!isBrowser) {
            if (window.ReactNativeWebView !== undefined)
              window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'userInfo', data: res }));
            else if (window.Android !== undefined)
              window.Android.postMessage(JSON.stringify({ type: 'userInfo', data: res }));
          }
        } catch (e) {
          logger.log(e);
        }
      },
      error => {
        toast.error('Fail! Please try again login');
        
        // Pinpoint 로그 기록 - 빠른 로그인 실패
        if(!isNativeFlag) {
          recordLoginEvent('quick_retry', false, { 
            userId, 
            storeId: 'unknown',
            retryCount: retryCount + 1,
            error: error.message || 'Unknown error'
          });
        } else {
          if (window.Android !== undefined)
            window.Android.postMessage(JSON.stringify({ type: 'attr', data: {function: 'quick_retry', userId, storeId: 'unknown', retryCount: retryCount + 1, error: error.message || 'Unknown error'} }));
        }
        
        setRetryCount((prev) => prev + 1);
        if (retryCount + 1 >= MAX_RETRY_COUNT) {
          toast.error("Quick login failed after maximum attempts.");
          setLoading(false);
        } else {
          // 재시도 로직
          setTimeout(() => quickLoginWithRetry(userId), 2000); // 2초 후 재시도
        }
      }
    );
  }

  const handleLogin = async (formValue: { account: string; password: string }) => {
    const { account, password } = formValue;
    logger.log(account,password)
    
    await (userLogin as any)(account, password).then(
      (res: any) => {
        // Pinpoint 로그 기록 - 일반 로그인 성공
        if(!isNativeFlag) {
          recordLoginEvent('user', true, { 
            account: account || 'unknown', 
            storeId: res?.storeId || 'unknown',
            storeName: res?.storeName || 'unknown' 
          });
        } else {
          if (window.Android !== undefined)
            window.Android.postMessage(JSON.stringify({ type: 'attr', data: {function: 'user', account: account || 'unknown', storeId: res?.storeId || 'unknown', storeName: res?.storeName || 'unknown'} }));
        }
        
        if(res.isAyce) {
          setIsAyce(res.isAyce)
        }
        logger.log(res)
        setUserInfo(res);
        setIsLogin(true);

        loadNamespace(`${res.storeId}`);
        coreInit(initOptions);
        toast.success(`Welcome you are logged in to ${res.storeName}`);
        navigate("/storesetting");
        try {
          if(!isBrowser){
            if(window.ReactNativeWebView !== undefined)
              window.ReactNativeWebView.postMessage(JSON.stringify({type: 'userInfo', data: res}));
            else if(window.Android !== undefined)
              window.Android.postMessage(JSON.stringify({type: 'userInfo', data: res}));
          }
        } catch(e) {
          logger.log(e)
        }
      },
      (error: any) => {
        const resMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();
          
        // Pinpoint 로그 기록 - 일반 로그인 실패
        if(!isNativeFlag) {
          recordLoginEvent('user', false, { 
            account: account || 'unknown', 
            storeId: 'unknown',
            error: resMessage || 'Unknown error' 
          });
        } else {
          if (window.Android !== undefined)
            window.Android.postMessage(JSON.stringify({ type: 'attr', data: {function: 'user', account: account || 'unknown', storeId: 'unknown', error: resMessage || 'Unknown error'} }));
        }
        
        toast.error('Please check your username or password again');
      }
    );
  };
  
  const formik = useFormik({
    initialValues: {
      account: '',
      password: '',
      submit: null
    },
    validationSchema: Yup.object({
      account: Yup
        .string().lowercase()
        .max(30)
        .required('UserName is required'),
      password: Yup
        .string()
        .max(30)
        .required('Password is required')
    }),
    onSubmit:  async (values: { account: string; password: string; }, helpers: any) => {handleLogin(values)}
  });
    // if (loading) {
    //   // 로딩 중일 때는 로그인 페이지를 보여주지 않고 로딩 스피너만 표시
    //   return (
    //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    //       <CircularProgress />
    //     </Box>
    //   );
    // }
    return (
      // <Dialog full Screen  open={open}>
        <Box
          sx={{
            backgroundColor: 'background.paper',
            flex: '1 1 auto',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            // mb: '20rem'
          }}
        >
          <Card
            sx={{
              width: '46.8rem',
              height: '24.6rem',
              padding: '2.9rem'
            }}
          >
            <div>
              <form
                noValidate
                onSubmit={formik.handleSubmit}
              >
                <Stack sx={{ alignItems: 'center', justifyContent: 'flex-start'}}>
                  <Stack
                  spacing={1}
                  sx={{ alignItems: 'center', mt: '1.6rem' }}
                 > 
                    <img style={{ width: '9.0133rem', height: '4rem' }} src={process.env.PUBLIC_URL+'/assets/images/logo.svg'} alt="logo"/>
                  </Stack>
                  <Box  sx={{display: 'flex', flexDirection: 'row'}}>
                    <Box  sx={{ width: '27.6rem'}}>
                      <Typography sx={{ fontSize: '1.4rem', fontWeight: '500'}}>ID</Typography>
                      <input  style={{
                        height: '3.6rem', 
                        width: '27.6rem',
                        fontSize: '16px',
                        WebkitTextSizeAdjust: '100%',
                        WebkitAppearance: 'none',
                        appearance: 'none',
                        borderRadius: '0.4rem',
                        border: '1px solid #ccc',
                        padding: '0 1rem'
                      }}
                        id={'loginAccount'}
                        name="account"
                        autoCapitalize='none'
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        type="account"
                        value={formik.values.account}
                      />
                      <Typography sx={{ mt: '1.0rem' , fontSize: '1.4rem', fontWeight: '500'}}>Password</Typography>
                      <input  style={{
                        height: '3.6rem', 
                        width: '27.6rem',
                        fontSize: '16px',
                        WebkitTextSizeAdjust: '100%',
                        WebkitAppearance: 'none',
                        appearance: 'none',
                        borderRadius: '0.4rem',
                        border: '1px solid #ccc',
                        padding: '0 1rem'
                      }}
                        id={'loginPassword'}
                        name="password"
                        autoCapitalize='none'
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        type="password"
                        value={formik.values.password}
                      />
                    </Box>
                    <Stack sx={{alignItems: 'flex-end', justifyContent:'flex-end'}}>
                      <Button
                        id={'login-submit'}
                        size="large"
                        sx={{ width: '12rem', height: '10.7rem',  borderRadius: '1rem', textTransform: 'none', fontSize: '1.6rem', fontWeight: '600', left: '1.4rem' }}
                        type="submit"
                        variant="contained"
                      >
                        Log in
                      </Button>
                    </Stack>
                    {formik.errors.submit && (
                      <Typography
                        color="error"
                        sx={{ mt: '0.5rem' }}
                        variant="body2"
                      >
                        {formik.errors.submit}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </form>
            </div>
          </Card>
        </Box>
      // </Dialog>
    );
  }


export default Login;
